import { useCallback, useEffect, useState } from 'react';
import { parsearFilasCsv } from '@tanteo/compartido';
import {
  CATALOGOS_ADMINISTRABLES,
  guardarCatalogoPlataforma,
  listarCatalogoPlataforma,
} from '../../lib/api-panel';
import { descargarCSV } from '../../ui/base/descargar';
import { Boton, BotonArchivo, Campo, EncabezadoTabla, Esqueleto, FilaTabla, GrupoOpciones, TablaAncha, Tarjeta, ThAcciones } from '../../ui/base';

type Catalogo = (typeof CATALOGOS_ADMINISTRABLES)[number];

export function Catalogos({ puedeEscribir }: { puedeEscribir: boolean }) {
  const [actual, setActual] = useState<Catalogo>(CATALOGOS_ADMINISTRABLES[0]);
  const [filas, setFilas] = useState<{ clave: string; nombre: string }[] | null>(null);
  const [clave, setClave] = useState('');
  const [nombre, setNombre] = useState('');
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    setFilas(null);
    listarCatalogoPlataforma(actual.tabla)
      .then(setFilas)
      .catch((e) => {
        setFilas([]);
        setAviso(e instanceof Error ? e.message : 'No pudimos cargar el catálogo.');
      });
  }, [actual]);
  useEffect(cargar, [cargar]);

  async function guardar(c: string, n: string) {
    setOcupado(true);
    setAviso('');
    try {
      await guardarCatalogoPlataforma(actual.tabla, c, n);
      setClave('');
      setNombre('');
      cargar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setOcupado(false);
    }
  }

  /** El CSV entra fila por fila para que un error en una no descarte las demás. */
  async function importar(archivo: File | undefined) {
    if (!archivo) return;
    setOcupado(true);
    setAviso('');
    try {
      const texto = await archivo.text();
      const filasCsv = parsearFilasCsv(texto).filter((f) => f.length >= 2 && f[0]?.trim());
      let guardadas = 0;
      const fallos: string[] = [];
      for (const [c, n] of filasCsv) {
        if (c!.trim().toLowerCase() === actual.clave.toLowerCase()) continue; // cabecera
        try {
          await guardarCatalogoPlataforma(actual.tabla, c!.trim(), (n ?? '').trim());
          guardadas += 1;
        } catch (e) {
          fallos.push(`${c}: ${e instanceof Error ? e.message : 'error'}`);
        }
      }
      setAviso(
        `${guardadas} fila${guardadas === 1 ? '' : 's'} guardada${guardadas === 1 ? '' : 's'}` +
          (fallos.length ? ` · ${fallos.length} con error: ${fallos.slice(0, 3).join(' | ')}` : '')
      );
      cargar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      {/* Tres bloques con nombre, en vez de tres botoneras seguidas sin explicar qué es cada una:
          qué catálogo se edita, qué se hace con el archivo, y la tabla. */}
      <p className="text-muted mb-1 text-etiqueta font-bold uppercase tracking-wide">Catálogo</p>
      <GrupoOpciones
        etiqueta="Catálogo a editar"
        className="mb-4"
        valor={actual.tabla}
        onElegir={(tabla) => setActual(CATALOGOS_ADMINISTRABLES.find((c) => c.tabla === tabla)!)}
        opciones={CATALOGOS_ADMINISTRABLES.map((c) => ({
          valor: c.tabla,
          texto: c.etiqueta,
          e2e: 'tab-catalogo',
        }))}
      />

      <p className="text-muted mb-1 text-etiqueta font-bold uppercase tracking-wide">Archivo</p>
      <div className="mb-1 flex flex-wrap gap-2">
        <Boton
          variante="contorno"
          onClick={() =>
            descargarCSV(
              `${actual.tabla}.csv`,
              [actual.clave, actual.valor],
              (filas ?? []).map((f) => [f.clave, f.nombre])
            )
          }
          disabled={!filas?.length}
        >
          Exportar CSV
        </Boton>
        {puedeEscribir && (
          <BotonArchivo
            variante="contorno"
            accept=".csv,text/csv"
            onChange={(e) => void importar(e.currentTarget.files?.[0])}
            data-e2e="catalogo-csv"
          >
            Importar CSV
          </BotonArchivo>
        )}
      </div>
      <p className="text-muted mb-4 text-contenido">
        Estructura del archivo: <span className="num font-semibold">{actual.clave}, {actual.valor}</span> — una fila por
        registro, separadas por coma. Al importar, una clave existente se actualiza y una nueva se agrega; nada se borra.
      </p>

      {aviso && <p className="text-info bg-info/10 rounded-control mb-3 px-3 py-2 text-contenido font-semibold">{aviso}</p>}

      <p className="text-muted mb-1 text-etiqueta font-bold uppercase tracking-wide">Registros</p>

      {filas === null ? (
        <Esqueleto lineas={6} />
      ) : (
        <TablaAncha>
            <EncabezadoTabla>
                <th className="py-2 pr-2">{actual.clave}</th>
                <th className="py-2 pr-2">{actual.valor}</th>
                <ThAcciones className="w-24" />
            </EncabezadoTabla>
            <tbody>
              {filas.map((f) => (
                <FilaTabla key={f.clave} data-e2e="fila-catalogo">
                  <td className="num py-2 pr-2 font-bold">{f.clave}</td>
                  <td className="py-2 pr-2">{f.nombre}</td>
                  <td className="py-2 text-right">
                    {puedeEscribir && (
                      <button
                        className="text-info underline"
                        onClick={() => {
                          setClave(f.clave);
                          setNombre(f.nombre);
                        }}
                      >
                        Editar
                      </button>
                    )}
                  </td>
                </FilaTabla>
              ))}
            </tbody>
          </TablaAncha>
      )}

      {puedeEscribir && (
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
          <Campo etiqueta={actual.clave} value={clave} onChange={(e) => setClave(e.target.value)} data-e2e="catalogo-clave" />
          <Campo etiqueta={actual.valor} value={nombre} onChange={(e) => setNombre(e.target.value)} data-e2e="catalogo-valor" />
          <div className="flex items-end">
            <Boton onClick={() => void guardar(clave, nombre)} disabled={ocupado || !clave.trim() || !nombre.trim()}>
              Guardar fila
            </Boton>
          </div>
        </div>
      )}
    </Tarjeta>
  );
}
